import React from 'react';

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorLocation: ''
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Dashboard Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);

    // Parse the error to get more descriptive information
    let errorLocation = 'Unknown location';
    let detailedMessage = error.toString();

    if (error.stack) {
      // Try to extract the component where error occurred
      const stackLines = error.stack.split('\n');
      const componentLine = stackLines.find(line =>
        line.includes('DashboardGrid') ||
        line.includes('WidgetCard') ||
        line.includes('GridLayout') ||
        line.includes('PriceChartWidget')
      );

      if (componentLine) {
        errorLocation = componentLine.trim();
      }
    }

    // Check for specific react-grid-layout errors
    if (detailedMessage.includes("Cannot read properties of null (reading 'props')")) {
      detailedMessage = `Widget Layout Error: One or more widgets have invalid configuration. This typically happens when:
        1. A widget is missing required data (id, ticker, or position)
        2. The grid layout received null or undefined children
        3. Widget data is corrupted in the database`;
    }

    this.setState({
      error,
      errorInfo,
      errorLocation,
      detailedMessage
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 m-4">
          <h2 className="text-xl font-bold text-red-400 mb-4">
            Dashboard Error - Something went wrong
          </h2>

          <div className="space-y-4">
            <div className="bg-background/50 p-4 rounded">
              <h3 className="font-semibold text-yellow-400 mb-2">Error Message:</h3>
              <p className="text-text/80 font-mono text-sm whitespace-pre-wrap">
                {this.state.detailedMessage || this.state.error?.toString()}
              </p>
            </div>

            <div className="bg-background/50 p-4 rounded">
              <h3 className="font-semibold text-yellow-400 mb-2">What you can do:</h3>
              <ul className="list-disc list-inside text-text/80 space-y-1">
                <li>Try refreshing the page</li>
                <li>Clear your browser cache</li>
                <li>Check if all widgets have valid ticker symbols</li>
                <li>Try creating a new dashboard if this one is corrupted</li>
              </ul>
            </div>

            <details className="bg-background/50 p-4 rounded">
              <summary className="font-semibold text-yellow-400 cursor-pointer">
                Technical Details (click to expand)
              </summary>
              <div className="mt-4 space-y-2">
                <div>
                  <strong className="text-text/60">Location:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto">
                    {this.state.errorLocation}
                  </pre>
                </div>
                <div>
                  <strong className="text-text/60">Stack Trace:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto">
                    {this.state.error?.stack}
                  </pre>
                </div>
                <div>
                  <strong className="text-text/60">Component Stack:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto">
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </div>
            </details>

            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-dark px-4 py-2 rounded hover:bg-accent transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default DashboardErrorBoundary;